/* Same-source previews; native responsive originals remain the no-JS fallback. */
(() => {
  'use strict';
  if (window.NorthyardProgressive || window.nyProgressiveFallback) return;
  if (!window.IntersectionObserver || !HTMLImageElement.prototype.decode) return;
  const records = new Map();
  const prepared = new WeakMap();
  const queued = new Set(), prefetching = new Set(), foreground = new Set(), swatches = new Set();
  let pumpFrame = 0, scrollPosition = scrollY, direction = 1, selectedWork = 0;
  const connection = navigator.connection;
  const reducedData = () => !!connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '');
  const schedulePump = () => {
    if (!pumpFrame) pumpFrame = requestAnimationFrame(() => { pumpFrame = 0; pump(); });
  };
  const release = r => { queued.delete(r); prefetching.delete(r); foreground.delete(r); schedulePump(); };
  const pump = () => {
    if (document.hidden) return;
    // Finish what the shopper can see before starting further background HD.
    // Already-started images keep downloading; scrolling never restarts them.
    if (selectedWork || [...foreground].some(r => r.inView && !hidden(r.image)
      && !['ready', 'error'].includes(r.image.dataset.nyState))) return;
    const pending = [...queued].map(r => {
      const box = r.image.getBoundingClientRect();
      const behind = direction > 0 ? box.bottom <= 0 : box.top >= innerHeight;
      return { r, distance: Math.max(0, box.top - innerHeight, -box.bottom), behind };
    }).sort((a, b) => Number(a.behind) - Number(b.behind) || a.distance - b.distance);
    for (const { r, distance, behind } of pending) {
      if (prefetching.size >= (reducedData() ? 1 : 3)) break;
      if (records.get(r.image) !== r || !r.image.isConnected || !r.near || r.probe
        || ['ready', 'error'].includes(r.image.dataset.nyState)) { queued.delete(r); continue; }
      if (hidden(r.image) || (behind && distance > (reducedData() ? 0 : 100))) continue;
      queued.delete(r);
      prefetching.add(r); upgrade(r, false, true);
    }
  };
  const sourceKey = image => [image.getAttribute('src'), image.srcset, image.sizes].join('|');
  const absolute = value => { try { return new URL(value, location.href).href; } catch { return ''; } };
  const smallSource = value => {
    try {
      const url = new URL(value, location.href);
      if (!['http:', 'https:'].includes(url.protocol)
        || (url.origin !== location.origin && url.hostname !== 'cdn.shopify.com')
        || !/\/(?:files|products|collections)\/.+\.(?:jpe?g|png|webp|avif)$/i.test(url.pathname)) return '';
      const cropHeight = Number(url.searchParams.get('height'));
      if (cropHeight) {
        const cropWidth = Number(url.searchParams.get('width'));
        if (!cropWidth) return '';
        url.searchParams.set('height', String(Math.max(1, Math.round(cropHeight * 120 / cropWidth))));
      }
      url.searchParams.set('width', '120');
      return url.href;
    } catch { return ''; }
  };
  const eligible = image => image instanceof HTMLImageElement && image.hasAttribute('data-ny-image');
  const hidden = image => document.hidden || !!image.closest('[hidden],dialog:not([open]),[data-qv-media-slide][aria-hidden="true"]');
  const hasPreview = image => {
    const r = records.get(image);
    return !!r && (r.backgroundReady || (image.complete && image.naturalWidth > 0 && absolute(image.currentSrc) === absolute(r.preview)));
  };
  const isReady = image => !eligible(image) || !image.dataset.nyState || ['ready', 'native'].includes(image.dataset.nyState);
  const signal = (r, state) => {
    r.image.dataset.nyState = state;
    r.image.dispatchEvent(new CustomEvent('northyard:image-state', { bubbles: true }));
  };
  const clearBackground = r => {
    if (!r.backgroundReady) return;
    r.image.style.backgroundImage = r.background.image;
    r.image.style.backgroundSize = r.background.size;
    r.image.style.backgroundPosition = r.background.position;
    r.image.style.backgroundRepeat = r.background.repeat;
    r.backgroundReady = false;
  };
  const stop = r => {
    release(r);
    ++r.version;
    if (r.probe) { r.probe.removeAttribute('srcset'); r.probe.removeAttribute('src'); r.probe = null; }
    if (r.small) { r.small.removeAttribute('src'); r.small = null; }
  };
  const valid = (r, version) => records.get(r.image) === r && r.version === version && r.image.isConnected;
  const fullReady = r => !r.gated && r.image.complete && r.image.naturalWidth > 0
    && (!r.gate.dataset.nyMobilePreview || new URL(r.image.currentSrc || r.image.src, location.href).pathname === new URL(r.preview, location.href).pathname);
  const finish = r => {
    release(r);
    prepared.delete(r.image);
    clearBackground(r); signal(r, 'ready');
    ahead.unobserve(r.image); near.unobserve(r.image); visible.unobserve(r.image);
  };
  const showPreview = r => {
    if (r.image.dataset.nyState === 'ready' || fullReady(r)) return;
    const style = getComputedStyle(r.image);
    r.backgroundReady = true;
    r.image.style.backgroundImage = 'url(' + JSON.stringify(r.preview) + ')';
    r.image.style.backgroundSize = style.objectFit === 'cover' ? 'cover' : 'contain';
    r.image.style.backgroundPosition = style.objectPosition;
    r.image.style.backgroundRepeat = 'no-repeat';
    // A broken original has complete=true too. Keep the decoded preview visible,
    // but retain the error state so Retry still retries HD, not commerce.
    if (r.image.dataset.nyState === 'error' && !r.gated) {
      r.gate.media = '(scripting: enabled)'; r.gated = true;
    }
    r.image.dispatchEvent(new CustomEvent('northyard:image-state', { bubbles: true }));
  };
  const interim = r => {
    if (r.gated || r.small || fullReady(r) || hidden(r.image)) return;
    const version = r.version, small = r.small = new Image();
    small.fetchPriority = 'low'; small.decoding = 'async'; small.src = r.preview;
    small.decode().then(() => {
      if (valid(r, version)) showPreview(r);
    }).catch(() => { /* A failed tiny preview must not block the original. */ });
  };
  const upgrade = (r, retry = false, speculative = false) => {
    if (!r || hidden(r.image) || !r.image.isConnected || (!retry && ['ready', 'error'].includes(r.image.dataset.nyState))) return;
    if (retry) { stop(r); signal(r, 'preview'); }
    if (!speculative) { queued.delete(r); prefetching.delete(r); foreground.add(r); }
    if (!r.gated) {
      if (retry) {
        // Gate with the known preview while a fresh full-image element retries.
        r.gate.media = '(scripting: enabled)'; r.gated = true;
      } else { if (!speculative) r.image.fetchPriority = 'high'; r.image.loading = 'eager'; interim(r); return; }
    }
    if (r.probe) { if (!speculative) r.probe.fetchPriority = 'high'; return; }
    const version = r.version, probe = r.probe = new Image();
    signal(r, 'loading');
    r.image.loading = 'eager';
    probe.fetchPriority = speculative ? 'low' : 'high'; probe.loading = 'eager'; probe.decoding = 'async';
    const branch = [...r.image.parentElement.querySelectorAll(':scope > source:not([data-ny-preview])')]
      .find(source => matchMedia(source.media || 'all').matches);
    probe.sizes = branch?.sizes || r.image.sizes;
    if (branch?.srcset || r.image.srcset) probe.srcset = branch?.srcset || r.image.srcset;
    probe.src = r.image.src;
    probe.decode().then(async () => {
      if (!valid(r, version)) return;
      r.gate.media = 'not all'; r.gated = false;
      // Decode only this image; never wait on a neighbor or the whole page.
      await Promise.resolve();
      await r.image.decode();
      if (valid(r, version)) { r.probe = null; finish(r); }
    }).catch(() => { if (valid(r, version)) { r.probe = null; release(r); signal(r, 'error'); } });
  };
  const ahead = new IntersectionObserver(entries => entries.forEach(({ target, isIntersecting }) => {
    const r = records.get(target);
    if (!r || !isIntersecting || hidden(target)) return;
    // Native loading starts the 120px source; the picture gate still holds HD.
    target.loading = 'eager';
    if (!r.gated) interim(r);
  }), { rootMargin: reducedData() ? '400px 80px' : '1000px 300px' });
  const near = new IntersectionObserver(entries => {
    entries.forEach(({ target, isIntersecting }) => {
      const r = records.get(target);
      if (!r) return;
      r.near = isIntersecting;
      if (!isIntersecting) queued.delete(r);
      else if (!r.gated) { target.loading = 'eager'; interim(r); }
      else if (!r.probe && !hidden(target)) queued.add(r);
    });
    schedulePump();
  }, { rootMargin: reducedData() ? '100px 0px' : '600px 80px' });
  const visible = new IntersectionObserver(entries => {
    entries.forEach(({ target, isIntersecting, intersectionRect }) => {
      const r = records.get(target);
      if (!r) return;
      r.inView = isIntersecting && intersectionRect.width > 0 && intersectionRect.height > 0;
      if (r.inView) upgrade(r);
      else {
        foreground.delete(r);
        if (r.probe) { r.probe.fetchPriority = 'low'; prefetching.add(r); }
      }
    });
    schedulePump();
  });
  const activateSwatch = swatch => {
    if (!swatch.isConnected || hidden(swatch) || !swatch.getClientRects().length
      || getComputedStyle(swatch).visibility !== 'visible') return;
    swatch.setAttribute('data-ny-swatch-ready', '');
    swatchNear.unobserve(swatch); swatches.delete(swatch);
  };
  const swatchNear = new IntersectionObserver(entries => entries.forEach(({ target, isIntersecting }) => {
    if (isIntersecting) activateSwatch(target);
  }), { rootMargin: reducedData() ? '100px 0px' : '600px 80px' });
  const observeSwatch = swatch => {
    if (swatch.hasAttribute('data-ny-swatch-ready') || swatches.has(swatch)) return;
    swatches.add(swatch); swatchNear.observe(swatch);
  };
  const register = (image, responsiveChange = false) => {
    if (!eligible(image)) return;
    const gate = image.parentElement?.querySelector(':scope > source[data-ny-preview]');
    if (!gate) { image.dataset.nyState = 'native'; return; }
    if (gate.dataset.nyMobilePreview) {
      gate.srcset = matchMedia('(max-width: 749px)').matches ? gate.dataset.nyMobilePreview : gate.dataset.nyDesktopPreview;
    }
    if (gate.hasAttribute('data-ny-rich')) {
      const preview = smallSource(image.getAttribute('src'));
      if (!preview) { gate.media = 'not all'; image.dataset.nyState = 'native'; return; }
      gate.srcset = preview;
    }
    const preview = gate.srcset, key = sourceKey(image) + '|' + preview;
    const old = records.get(image);
    if (old?.key === key && !responsiveChange) return;
    if (old) { stop(old); clearBackground(old); ahead.unobserve(image); near.unobserve(image); visible.unobserve(image); }
    const r = { image, gate, preview, key, version: 0, gated: matchMedia(gate.media || 'all').matches,
      background: { image: image.style.backgroundImage, size: image.style.backgroundSize,
        position: image.style.backgroundPosition, repeat: image.style.backgroundRepeat } };
    records.set(image, r);
    if (fullReady(r)) { finish(r); return; }
    const seed = prepared.get(image);
    const matchingSeed = seed?.key === sourceKey(image) && seed.preview === preview;
    signal(r, matchingSeed && seed.error ? 'error' : r.gated ? 'preview' : 'loading');
    if (matchingSeed && seed.ready) showPreview(r);
    ahead.observe(image); near.observe(image); visible.observe(image);
    if (!r.gated) interim(r);
  };
  const scan = root => {
    if (!(root instanceof Element)) return;
    if (root.matches('[data-ny-swatch]')) observeSwatch(root);
    root.querySelectorAll('[data-ny-swatch]').forEach(observeSwatch);
    if (eligible(root)) register(root);
    // Do not pass NodeList's numeric index as register's responsive-change flag.
    root.querySelectorAll('img[data-ny-image]').forEach(image => register(image));
  };
  const wrap = (image, preview, eager = false) => {
    if (!preview || !smallSource(preview)) return image;
    let picture = image.closest('picture.ny-progressive');
    if (!picture) {
      picture = document.createElement('picture'); picture.className = 'ny-progressive';
      const source = document.createElement('source'); source.dataset.nyPreview = '';
      source.srcset = preview; source.media = eager ? 'not all' : '(scripting: enabled)';
      picture.append(source);
      image.replaceWith(picture); picture.append(image);
    }
    image.dataset.nyImage = '';
    if (image.isConnected && image.getAttribute('src')) register(image);
    return picture;
  };
  // A selected media slot can be committed once either its exact tiny preview
  // or its original decodes. Retain this same HD element across the DOM commit.
  const prepare = async (image, { signal: abort, onSlow } = {}) => {
    if (!image) return;
    if (abort?.aborted) throw new DOMException('Aborted', 'AbortError');
    image.fetchPriority = 'high'; image.loading = 'eager'; image.decoding = 'async';
    if (image.complete && image.naturalWidth > 0) return;
    const preview = smallSource(image.src);
    if (!preview) return window.Northyard.ProductState.prepareImage(image, { signal: abort, onSlow });
    const seed = { preview, key: sourceKey(image), ready: false, error: false };
    ++selectedWork;
    let released = false;
    const releaseSelected = () => { if (!released) { released = true; --selectedWork; schedulePump(); } };
    prepared.set(image, seed);
    const small = new Image(); small.fetchPriority = 'high'; small.decoding = 'async'; small.src = preview;
    let cancelled = false, cancel;
    const aborted = new Promise((_, reject) => { cancel = () => {
      cancelled = true;
      releaseSelected();
      if (!image.isConnected) { image.removeAttribute('srcset'); image.removeAttribute('src'); }
      small.removeAttribute('src'); reject(new DOMException('Aborted', 'AbortError'));
    }; });
    abort?.addEventListener('abort', cancel, { once: true });
    const hd = image.decode().finally(releaseSelected).catch(error => {
      if (!cancelled && prepared.get(image) === seed && sourceKey(image) === seed.key) seed.error = true;
      throw error;
    });
    const tiny = small.decode().then(() => {
      if (cancelled) throw new DOMException('Aborted', 'AbortError');
      if (prepared.get(image) !== seed || sourceKey(image) !== seed.key) return;
      seed.ready = true;
      const r = records.get(image);
      if (r?.preview === preview && image.isConnected) showPreview(r);
    });
    const timer = setTimeout(() => { if (!cancelled) onSlow?.(); }, 12000);
    try { await Promise.race([Promise.any([hd, tiny]), aborted]); }
    finally { releaseSelected(); clearTimeout(timer); abort?.removeEventListener('abort', cancel); }
  };
  document.addEventListener('load', event => {
    const r = records.get(event.target);
    if (!r) return;
    if (fullReady(r)) finish(r);
    else r.image.dispatchEvent(new CustomEvent('northyard:image-state', { bubbles: true }));
  }, true);
  document.addEventListener('error', event => {
    const r = records.get(event.target);
    if (!r) return;
    if (r.gated) upgrade(r); else {
      release(r);
      signal(r, 'error');
      if (r.backgroundReady) showPreview(r); else interim(r);
    }
  }, true);
  const observer = new MutationObserver(changes => {
    for (const change of changes) {
      if (change.type === 'childList') change.addedNodes.forEach(scan);
      else if (eligible(change.target)) register(change.target);
      else if (change.attributeName === 'open' || change.attributeName === 'hidden') {
        for (const r of records.values()) if (change.target.contains(r.image)) {
          if (hidden(r.image)) { stop(r); if (r.image.dataset.nyState !== 'ready') signal(r, 'preview'); }
          else { ahead.unobserve(r.image); near.unobserve(r.image); visible.unobserve(r.image); ahead.observe(r.image); near.observe(r.image); visible.observe(r.image); }
        }
        for (const swatch of swatches) if (change.target.contains(swatch)) {
          swatchNear.unobserve(swatch); swatchNear.observe(swatch);
        }
      }
    }
    for (const [image, r] of records) if (!image.isConnected) {
      stop(r); ahead.unobserve(image); near.unobserve(image); visible.unobserve(image); records.delete(image);
    }
    for (const swatch of swatches) if (!swatch.isConnected) { swatchNear.unobserve(swatch); swatches.delete(swatch); }
  });
  observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'srcset', 'sizes', 'open', 'hidden'] });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      for (const r of records.values()) {
        ahead.unobserve(r.image); near.unobserve(r.image); visible.unobserve(r.image); ahead.observe(r.image); near.observe(r.image); visible.observe(r.image);
      }
      for (const swatch of swatches) { swatchNear.unobserve(swatch); swatchNear.observe(swatch); }
      schedulePump();
    }
  });
  document.addEventListener('scroll', () => {
    const position = scrollY;
    if (Math.abs(position - scrollPosition) > 8) {
      direction = position > scrollPosition ? 1 : -1; scrollPosition = position;
    }
    schedulePump();
  }, { passive: true });
  ['pointerover', 'focusin', 'pointerdown'].forEach(type => document.addEventListener(type, event => {
    const card = event.target.closest?.('[data-product-card]');
    if (card) card.querySelectorAll('[data-ny-swatch]:not([data-ny-swatch-ready])').forEach(activateSwatch);
  }, { passive: true }));
  matchMedia('(max-width: 749px)').addEventListener('change', () => {
    for (const r of records.values()) if (r.gate.dataset.nyMobilePreview) {
      r.gate.media = 'not all'; register(r.image, true);
    }
  });
  window.NorthyardProgressive = { scan, wrap, prepare, smallSource, hasPreview, isReady,
    cleanClone(image, clone) {
      const r = records.get(image);
      if (r?.backgroundReady) {
        clone.style.backgroundImage = r.background.image; clone.style.backgroundSize = r.background.size;
        clone.style.backgroundPosition = r.background.position; clone.style.backgroundRepeat = r.background.repeat;
      }
      clone.removeAttribute('data-ny-state');
    },
    set(image, { src, srcset = '', sizes = '', preview = smallSource(src) }) {
      prepared.delete(image);
      const r = records.get(image);
      if (r) { stop(r); clearBackground(r); ahead.unobserve(image); near.unobserve(image); visible.unobserve(image); records.delete(image); }
      const picture = wrap(image, preview);
      const gate = picture.querySelector?.('source[data-ny-preview]');
      if (gate) { gate.srcset = preview; gate.media = preview ? '(scripting: enabled)' : 'not all'; }
      image.sizes = sizes; image.srcset = srcset; image.src = src;
      if (preview) register(image); else image.dataset.nyState = 'native';
    },
    upgrade(image) { register(image); upgrade(records.get(image)); },
    retry(image) { const r = records.get(image); if (!r) return false; upgrade(r, true); return true; } };
  scan(document.body);
  window.NorthyardProgressive.ready = true;
})();
